/**
 * Calendar Month View
 * Display appointments in a monthly grid
 */

import type { AppointmentWithDetails } from '../../hooks/useAppointments';
import {
  getMonthCalendar,
  isSameDay,
  isToday,
  getIcelandicWeekdayShort,
  getStatusColor,
  formatTime,
  parseISODateTime,
} from '../../utils/calendar';

interface CalendarMonthViewProps {
  year: number;
  month: number;
  appointments: AppointmentWithDetails[];
  onAppointmentClick: (appointment: AppointmentWithDetails) => void;
  onDateClick: (date: Date) => void;
}

export function CalendarMonthView({
  year,
  month,
  appointments,
  onAppointmentClick,
  onDateClick,
}: CalendarMonthViewProps) {
  const calendar = getMonthCalendar(year, month);

  // Get appointments for a specific day
  const getAppointmentsForDay = (day: Date) => {
    return appointments.filter((apt) => {
      const aptDate = parseISODateTime(apt.appointment_datetime);
      return isSameDay(aptDate, day);
    });
  };

  // Check if date is in current month
  const isCurrentMonth = (date: Date) => date.getMonth() === month;

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      {/* Weekday headers */}
      <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50">
        {[0, 1, 2, 3, 4, 5, 6].map((day) => (
          <div key={day} className="p-2 text-center text-sm font-medium text-gray-700">
            {getIcelandicWeekdayShort(day === 0 ? 0 : day)}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7">
        {calendar.map((week, weekIndex) =>
          week.map((day, dayIndex) => {
            const dayAppointments = getAppointmentsForDay(day);
            const inCurrentMonth = isCurrentMonth(day);
            const today = isToday(day);

            return (
              <div
                key={`${weekIndex}-${dayIndex}`}
                className={`min-h-[120px] border-b border-r border-gray-200 p-2 ${
                  !inCurrentMonth ? 'bg-gray-50' : 'bg-white'
                } hover:bg-blue-50 cursor-pointer transition-colors`}
                onClick={() => onDateClick(day)}
              >
                {/* Day number */}
                <div
                  className={`text-sm font-medium mb-1 ${
                    !inCurrentMonth
                      ? 'text-gray-400'
                      : today
                        ? 'text-blue-600 font-bold'
                        : 'text-gray-900'
                  }`}
                >
                  {day.getDate()}
                </div>

                {/* Appointments */}
                <div className="space-y-1 overflow-y-auto max-h-[90px]">
                  {dayAppointments.slice(0, 3).map((apt) => {
                    const statusColors = getStatusColor(apt.status);
                    return (
                      <div
                        key={apt.id}
                        className={`text-xs px-1.5 py-0.5 rounded border-l-2 ${statusColors.border} ${statusColors.bg} truncate cursor-pointer ${statusColors.hover}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onAppointmentClick(apt);
                        }}
                      >
                        <div className="font-medium truncate">
                          {formatTime(apt.appointment_datetime)} {apt.patient_name}
                        </div>
                      </div>
                    );
                  })}
                  {dayAppointments.length > 3 && (
                    <div className="text-xs text-gray-500 text-center">
                      +{dayAppointments.length - 3} meira
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
